/**
 * 文件说明:
 *
 * hz menu data and antd Menu component
 *
 * ----------------------------------------
 * 创建用户: leo
 */
import {useCallback, memo, useMemo, useEffect} from 'react'
import {Menu} from 'antd'
import {useSafeState, useRefData} from 'hooks'
import Icon from 'components/icon'
import classnames from 'classnames'
import {Link} from 'umi'
import {checkPathList, createMemoizeCache, urlToList} from 'utils'
import styles from './index.less'

const {SubMenu} = Menu

const getIcon = (icon) => {
    if (typeof icon === 'string' && icon !== '') {
        if (icon.indexOf('http') === 0) {
            return <img src={icon} alt="icon" className={`${styles.icon} sider-menu-item-img`} />
        }

        return <Icon type={icon} className={styles.iconSvg} />
    }

    return icon
}

const conversionPath = (path) => {
    if (path && path.indexOf('http') === 0) {
        return path
    }
    return `/${path || ''}`.replace(/\/+/g, '/')
}

const findSelectItem = createMemoizeCache({
    isEqual() {
        return true
    },
    getKey({pathname}) {
        return pathname
    },
    getDeps({flatMenuData}) {
        return flatMenuData
    },
    getVal({pathname, flatMenuData}) {
        const urlToListArr = urlToList(pathname)

        for (const item of flatMenuData) {
            if (checkPathList(item.path, urlToListArr)) {
                return item
            }
        }

        return false
    },
})

const BaseMenu = memo((props) => {
    const {menuData, pathname, flatMenuData, className} = props

    const [openKeys, setOpenKeys] = useSafeState([])
    const [selectedKeys, setSelectedKeys] = useSafeState([])

    const getRefData = useRefData({flatMenuData, setOpenKeys, setSelectedKeys, selectedKeys})

    useEffect(() => {
        const selectItem = findSelectItem({pathname, flatMenuData: getRefData().flatMenuData})

        if (selectItem) {
            const newOenKeys = [...(selectItem?.parents ?? [])]
            getRefData().setOpenKeys(newOenKeys)
            if (getRefData()?.selectedKeys?.[0] !== selectItem?.key) {
                const newSelectedKeys = [selectItem?.key ?? '']
                getRefData().setSelectedKeys(newSelectedKeys)
            }
        }
    }, [pathname])

    const onOpenChange = useCallback(
        (keys) => {
            setOpenKeys(keys)
        },
        [setOpenKeys],
    )

    const getMenuItemPath = useCallback(
        (item) => {
            const itemPath = conversionPath(item.path)
            const selected = selectedKeys.indexOf(item.path) > -1
            const icon = getIcon(item.icon, selected)
            const {target, name} = item
            if (/^https?:\/\//.test(itemPath)) {
                return (
                    <a href={itemPath} target={target} className="hz-ant-menu-item-selected-btn">
                        {icon}
                        <span>{name}</span>
                    </a>
                )
            }

            return (
                <Link
                    to={itemPath}
                    target={target}
                    replace={itemPath === pathname}
                    className="hz-ant-menu-item-selected-btn"
                >
                    {icon}
                    <span>{name}</span>
                </Link>
            )
        },
        [pathname, selectedKeys],
    )

    const getNavMenuItems = useCallback(
        (menusData) => {
            if (!menusData) {
                return []
            }

            const getSubMenuOrItem = (item) => {
                if (Array.isArray(item.children) && item.children.some((child) => child.name)) {
                    const childrenItems = getNavMenuItems(item.children, selectedKeys)
                    if (childrenItems && childrenItems.length > 0) {
                        return (
                            <SubMenu
                                title={
                                    item.icon ? (
                                        <span>
                                            {getIcon(item.icon)}
                                            <span>{item.name}</span>
                                        </span>
                                    ) : (
                                        item.name
                                    )
                                }
                                key={item.key}
                            >
                                {childrenItems}
                            </SubMenu>
                        )
                    }
                    return null
                }

                const clx = classnames({
                    // [styles.menuItem]: true,
                    'hz-ant-menu-home-item': item.key === 'crm_home',
                })

                return (
                    <Menu.Item key={item.key} className={clx}>
                        {getMenuItemPath(item)}
                    </Menu.Item>
                )
            }

            return menusData
                .filter((item) => item.name && !item.hideInMenu)
                .map((item) => getSubMenuOrItem(item))
                .filter((item) => item)
        },
        [getMenuItemPath, selectedKeys],
    )

    const navMenuItems = useMemo(() => getNavMenuItems(menuData), [menuData, getNavMenuItems])

    return (
        <Menu
            className={classnames(className, styles.main)}
            key="Menu"
            mode="inline"
            theme="wi"
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            selectedKeys={selectedKeys}
            style={{width: '100%', height: '100%'}}
        >
            {navMenuItems}
        </Menu>
    )
})

export default BaseMenu
